/**
 * URL validation and sanitization utilities.
 *
 * Provides protection against open redirect vulnerabilities
 * and other URL-based attacks.
 */

// =============================================================================
// CONFIGURATION
// =============================================================================

/**
 * Whitelist of allowed external domains for redirects.
 * Add trusted domains here.
 */
const ALLOWED_EXTERNAL_DOMAINS = [
  // Educational / Church resources
  "bible-life.academy",
  "www.bible-life.academy",
  // Telegram
  "t.me",
  "telegram.me",
  // YouTube for streams
  "youtube.com",
  "www.youtube.com",
  "youtu.be",
  // VK for streams
  "vk.com",
  "www.vk.com",
  // Google Maps
  "maps.google.com",
  "www.google.com",
  "goo.gl",
  "maps.app.goo.gl",
  // Yandex Maps
  "yandex.ru",
  "maps.yandex.ru",
] as const;

/**
 * Protocols allowed in URLs.
 */
const ALLOWED_PROTOCOLS = ["https:", "http:", "tel:", "mailto:"] as const;

// =============================================================================
// VALIDATION FUNCTIONS
// =============================================================================

/**
 * Check if a URL is safe for external redirect.
 *
 * @param url - URL to validate
 * @returns True if URL is safe for redirect
 */
export function isAllowedExternalUrl(url: string): boolean {
  if (!url || typeof url !== "string") {
    return false;
  }

  try {
    const parsed = new URL(url);

    // Check protocol
    if (!ALLOWED_PROTOCOLS.includes(parsed.protocol as typeof ALLOWED_PROTOCOLS[number])) {
      return false;
    }

    // tel: and mailto: are always allowed (no domain check needed)
    if (parsed.protocol === "tel:" || parsed.protocol === "mailto:") {
      return true;
    }

    // Check domain against whitelist
    const hostname = parsed.hostname.toLowerCase();
    return ALLOWED_EXTERNAL_DOMAINS.some(
      (domain) => hostname === domain || hostname.endsWith(`.${domain}`)
    );
  } catch {
    // Invalid URL
    return false;
  }
}

/**
 * Check if a URL is a relative path (starts with /).
 * Relative paths are always safe for internal navigation.
 *
 * @param url - URL to check
 * @returns True if URL is a relative path
 */
export function isRelativePath(url: string): boolean {
  if (!url || typeof url !== "string") {
    return false;
  }

  // Must start with single / but not //
  return url.startsWith("/") && !url.startsWith("//");
}

/**
 * Check if a URL is safe for any redirect (internal or external).
 *
 * @param url - URL to validate
 * @returns True if URL is safe
 */
export function isSafeRedirectUrl(url: string): boolean {
  return isRelativePath(url) || isAllowedExternalUrl(url);
}

/**
 * Get a safe URL for redirect, or fallback to home.
 *
 * @param url - URL to validate
 * @param fallback - Fallback URL if invalid (default: "/")
 * @returns Safe URL for redirect
 */
export function getSafeRedirectUrl(url: string, fallback = "/"): string {
  if (isSafeRedirectUrl(url)) {
    return url;
  }
  return fallback;
}

/**
 * Sanitize a URL for display, removing potentially dangerous parts.
 *
 * @param url - URL to sanitize
 * @returns Sanitized URL or empty string if invalid
 */
export function sanitizeUrl(url: string | null | undefined): string {
  if (!url) return "";

  try {
    const parsed = new URL(url);

    // Only allow safe protocols
    if (!ALLOWED_PROTOCOLS.includes(parsed.protocol as typeof ALLOWED_PROTOCOLS[number])) {
      return "";
    }

    // Remove credentials
    parsed.username = "";
    parsed.password = "";

    return parsed.toString();
  } catch {
    // Check if it's a relative path
    if (isRelativePath(url)) {
      return url;
    }
    return "";
  }
}

// =============================================================================
// CONTACT VALIDATION
// =============================================================================

/**
 * Validate and sanitize a phone number for use in tel: href.
 * Removes non-digit characters except + at start.
 *
 * @param phone - Phone number to validate
 * @returns Sanitized phone for tel: href, or empty string if invalid
 */
export function sanitizePhone(phone: string | null | undefined): string {
  if (!phone) return "";

  // Remove all non-digit characters except + at the beginning
  const cleaned = phone.trim();
  if (!cleaned) return "";

  // Extract digits and optional leading +
  const hasPlus = cleaned.startsWith("+");
  const digits = cleaned.replace(/\D/g, "");

  // Require at least 7 digits for a valid phone
  if (digits.length < 7) return "";

  return hasPlus ? `+${digits}` : digits;
}

/**
 * Validate and sanitize an email for use in mailto: href.
 * Basic validation for format safety.
 *
 * @param email - Email to validate
 * @returns Sanitized email for mailto: href, or empty string if invalid
 */
export function sanitizeEmail(email: string | null | undefined): string {
  if (!email) return "";

  const cleaned = email.trim().toLowerCase();
  if (!cleaned) return "";

  // Basic email pattern: must have @ with something before and after
  // Also check for dangerous characters
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!emailRegex.test(cleaned)) return "";

  // Additional safety: no script injection via email
  if (cleaned.includes("<") || cleaned.includes(">") || cleaned.includes("'") || cleaned.includes('"')) {
    return "";
  }

  return cleaned;
}

// =============================================================================
// EXPORTS
// =============================================================================

export { ALLOWED_EXTERNAL_DOMAINS, ALLOWED_PROTOCOLS };
